/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback } from "react";

// internal-deps
import {
    ReviewedCartApiResponse,
    ApplyCouponsWidgetInfo,
    GiftingCartApiResponse,
    MROChildCart,
} from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import {
    AnalyticsContext,
    CheckoutActionTypes,
    CheckoutContext,
    CouponPageType,
    GroupOrderCartContext,
    UserContext,
} from "@swiggy-private/checkout-core";
import { CouponsAnalyticsExtraParams, CouponsAnalyticsExtraContext } from "@cart-client/interfaces/Coupon";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// hooks
import { useCartRendererAnalytics } from "./useCartRenderedAnalytics";
import { useCrossSellAnalytics } from "./useCrossSellAnalytics";
import { useCartRendererXP } from "./useCartRendererXP";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

// helpers
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { goToLogin } from "@cart-client/helpers/Login";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { isEmpty } from "@swiggy-private/js-utils";
import { getPaymentCouponCode, getRestaurantCouponCode } from "@swiggy-private/cart-ui";
import { getStaticMwebCartPath } from "@cart-client/helpers/Paths";
import { getMROChildCartFromApplyCouponWidgetInfo, isMROFlow } from "@cart-client/helpers/MROFoodCart";

// analytics
import {
    clickApplyCoupon,
    clickApplyCouponBtnEvent,
    clickTooltipOffercashEvent,
    clickViewMoreCouponsEvent,
} from "@cart-client/analytics/events";

// constants
import { SAVINGS_COUPON } from "@cart-client/constants/Coupon";
import {
    buildGiftingExtraParamsForSavingsCornerAnalytics,
    buildMROCouponCodeAnalyticsContext,
} from "@cart-client/helpers/Coupon";
import { extractGiftingCouponCodes, fetchCouponCodeListFromResponse } from "@cart-client/helpers/CouponListing";

type ApplyCouponWidgetData = ReviewedCartApiResponse["apply_coupon_widget_info"];

export interface UseCouponSavingsActionsReturnType {
    onViewAllCouponsClick: (
        isCouponLockedWidgetVariant: boolean,
        isPaymentCouponWidget?: boolean,
        applyCouponWidgetData?: ApplyCouponWidgetData,
    ) => void;
    onDirectApplyCouponCtaClick: (params: {
        isPaymentCouponWidget?: boolean;
        applyCouponWidgetData?: ApplyCouponWidgetData;
    }) => void;
    onCouponLockedLineItemLoad: VoidFunction;
    onDirectCouponLineItemLoad: (
        isPaymentCouponWidget?: boolean,
        applyCouponWidgetData?: ApplyCouponWidgetData,
    ) => void;
    onApplyCouponSectionClick: ({
        isPaymentCouponWidget,
        applyCouponWidgetData,
    }: {
        isPaymentCouponWidget?: boolean;
        applyCouponWidgetData?: ApplyCouponWidgetData;
    }) => void;
    onExtraSavingsTooltipClick: (applyCouponWidgetData?: ApplyCouponWidgetData) => void;
    goToCouponListing: (isPaymentCouponWidget?: boolean, applyCouponWidgetData?: ApplyCouponWidgetData) => void;
}

export interface IProps {
    // Payment coupon stays at the combined-cart level; restaurant coupon data now flows
    // per rendering detail through the action handlers (see params above).
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"];
}

export const useCouponSavingsActions = ({
    applyPaymentCouponWidgetData,
}: IProps): UseCouponSavingsActionsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reloadCart, syncApplyCouponCart } = useContext(UpdateCartContext);
    const { setUser } = useContext(UserContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    const { resetCouponListingPage } = useContext(CouponListingContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { data } = useContext(CartFlowContext);

    const { reviewedCart: cartResponse } = cartState;
    const isGiftingCart = isGiftingCartSource(data?.cartSource);
    const isMROCart = isMROFlow(data?.cartSource);

    const { xpSelectiveCouponVariant } = useCartRendererXP(cartResponse);
    const { onImpressionAddMoreAndSaveMore } = useCrossSellAnalytics(cartResponse);
    const { onImpressionDirectCouponWidgetEvent } = useCartRendererAnalytics(cartResponse);

    const userLoggedIn = isUserLoggedIn();
    const paymentCouponCode = getPaymentCouponCode(cartResponse?.data, applyPaymentCouponWidgetData);

    const getAnalyticsContextForCartTypes = useCallback(
        (rxId?: string | number): [CouponsAnalyticsExtraParams, CouponsAnalyticsExtraContext] => {
            if (isGiftingCart) {
                return [
                    buildGiftingExtraParamsForSavingsCornerAnalytics(cartResponse?.data as GiftingCartApiResponse),
                    fetchCouponCodeListFromResponse(cartResponse?.data as GiftingCartApiResponse),
                ];
            } else if (isMROCart) {
                return [buildMROCouponCodeAnalyticsContext(cartResponse as unknown as MROReviewedCart, rxId), {}];
            }
            return [{}, {}];
        },
        [cartResponse, isGiftingCart, isMROCart],
    );

    const groupCartId = groupCartState.data?.group_cart_id;

    const goToCouponListing = useCallback(
        (isPaymentCouponWidget?: boolean, applyCouponWidgetData?: ApplyCouponWidgetData) => {
            resetCouponListingPage();

            let actionType = CheckoutActionTypes.SHOW_COUPON_LISTING;
            if (isGiftingCart) {
                actionType = CheckoutActionTypes.SHOW_GIFTING_COUPON_LISTING;
            } else if (isMROCart) {
                actionType = CheckoutActionTypes.SHOW_MRO_FOOD_COUPON_LISTING;
            }
            setCheckoutState({
                type: actionType,
                payload: {
                    couponListingLaunchTimestamp: Date.now(),
                    couponListingType: isPaymentCouponWidget
                        ? CouponPageType.PAYMENT_COUPON_LISTING
                        : CouponPageType.RESTAURANT_COUPON_LISTING,
                    mroCouponListingRestaurantId: isMROCart ? applyCouponWidgetData?.mroChildCartRestaurantId : "",
                },
            });
        },
        [isGiftingCart, isMROCart, resetCouponListingPage, setCheckoutState],
    );
    const goToLoginFromSavings = useCallback(async () => {
        await goToLogin(SAVINGS_COUPON, reloadCart, setUser, getStaticMwebCartPath());
    }, [reloadCart, setUser]);

    const onApplyCouponSectionClick = useCallback(
        async ({
            isPaymentCouponWidget = false,
            applyCouponWidgetData,
        }: {
            isPaymentCouponWidget?: boolean;
            applyCouponWidgetData?: ApplyCouponWidgetData;
        }) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }

            const [analyticsExtraParams, analyticsContext] = getAnalyticsContextForCartTypes(
                applyCouponWidgetData?.mroChildCartRestaurantId,
            );
            clickApplyCoupon(clickEvent, {
                xpSelectiveCouponVariant,
                groupCartId,
                isPaymentCouponWidget,
                extraContext: analyticsContext,
                extraParams: analyticsExtraParams,
            });
            goToCouponListing(isPaymentCouponWidget, applyCouponWidgetData);
        },
        [
            userLoggedIn,
            getAnalyticsContextForCartTypes,
            clickEvent,
            xpSelectiveCouponVariant,
            groupCartId,
            goToCouponListing,
            goToLoginFromSavings,
        ],
    );

    const onCouponLockedLineItemLoad = useCallback(() => {
        onImpressionAddMoreAndSaveMore(true);
    }, [onImpressionAddMoreAndSaveMore]);

    const onViewAllCouponsClick = useCallback(
        async (
            isCouponLockedWidgetVariant: boolean,
            isPaymentCouponWidget?: boolean,
            applyCouponWidgetData?: ApplyCouponWidgetData,
        ) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }

            const [analyticsExtraParams, analyticsContext] = getAnalyticsContextForCartTypes(
                applyCouponWidgetData?.mroChildCartRestaurantId,
            );

            if (isCouponLockedWidgetVariant) {
                clickViewMoreCouponsEvent(clickEvent, {
                    groupCartId,
                    extraContext: analyticsContext,
                    extraParams: analyticsExtraParams,
                });
            } else {
                clickApplyCoupon(clickEvent, {
                    xpSelectiveCouponVariant,
                    groupCartId,
                    isPaymentCouponWidget,
                    extraContext: analyticsContext,
                    extraParams: analyticsExtraParams,
                });
            }

            goToCouponListing(isPaymentCouponWidget, applyCouponWidgetData);
        },
        [
            clickEvent,
            goToCouponListing,
            goToLoginFromSavings,
            groupCartId,
            userLoggedIn,
            xpSelectiveCouponVariant,
            getAnalyticsContextForCartTypes,
        ],
    );

    // Restaurant coupon code is derived from the scoped widget data passed in per rendering detail.
    const getCouponCode = useCallback(
        (isPaymentCouponWidget?: boolean, applyCouponWidgetData?: ApplyCouponWidgetData) => {
            if (isPaymentCouponWidget) {
                return paymentCouponCode;
            } else {
                let restaurantCouponCode = getRestaurantCouponCode(cartResponse?.data, applyCouponWidgetData);
                // if the cart is an MRO cart and the restaurant id is present in the apply coupon widget data
                // then get the child cart from the apply coupon widget info and fetch the coupon_code from that
                if (isMROCart) {
                    const childCart = getMROChildCartFromApplyCouponWidgetInfo(
                        cartResponse as MROReviewedCart,
                        applyCouponWidgetData,
                    );
                    if (!isEmpty(childCart)) {
                        restaurantCouponCode = getRestaurantCouponCode(
                            childCart as MROChildCart,
                            applyCouponWidgetData,
                        );
                    }
                }
                return restaurantCouponCode;
            }
        },
        [cartResponse, isMROCart, paymentCouponCode],
    );

    const getGiftingCouponCodes = useCallback(
        (applyCouponWidgetData?: ApplyCouponWidgetData) => {
            return isGiftingCartSource(data?.cartSource) ? extractGiftingCouponCodes(applyCouponWidgetData) : [];
        },
        [data?.cartSource],
    );

    const onDirectCouponLineItemLoad = useCallback(
        (isPaymentCouponWidget?: boolean, applyCouponWidgetData?: ApplyCouponWidgetData) => {
            const couponCode = getCouponCode(isPaymentCouponWidget, applyCouponWidgetData);
            if (!isEmpty(couponCode)) {
                onImpressionDirectCouponWidgetEvent(couponCode, isPaymentCouponWidget);
            }
        },
        [getCouponCode, onImpressionDirectCouponWidgetEvent],
    );

    const onDirectApplyCouponCtaClick = useCallback(
        async ({
            isPaymentCouponWidget = false,
            applyCouponWidgetData,
        }: {
            isPaymentCouponWidget?: boolean;
            applyCouponWidgetData?: ApplyCouponWidgetData;
        }) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }
            const couponCode = getCouponCode(isPaymentCouponWidget, applyCouponWidgetData);
            const giftingCouponCodes = getGiftingCouponCodes(applyCouponWidgetData);
            const [analyticsExtraParams, analyticsContext] = getAnalyticsContextForCartTypes(
                applyCouponWidgetData?.mroChildCartRestaurantId,
            );
            if (!isEmpty(couponCode)) {
                clickApplyCouponBtnEvent(clickEvent, {
                    couponCode,
                    groupCartId,
                    isPaymentCouponWidget,
                    extraParams: analyticsExtraParams,
                    extraContext: analyticsContext,
                });
                if (isMROCart) {
                    syncApplyCouponCart({
                        couponCode,
                        restaurantId: String(applyCouponWidgetData?.mroChildCartRestaurantId || ""),
                    });
                } else {
                    syncApplyCouponCart({ couponCode });
                }
            } else if (!isEmpty(giftingCouponCodes)) {
                clickApplyCouponBtnEvent(clickEvent, {
                    groupCartId,
                    isPaymentCouponWidget,
                    extraParams: analyticsExtraParams,
                    extraContext: analyticsContext,
                });
                syncApplyCouponCart({ appliedCouponCodes: giftingCouponCodes });
            }
        },
        [
            userLoggedIn,
            getCouponCode,
            getGiftingCouponCodes,
            getAnalyticsContextForCartTypes,
            goToLoginFromSavings,
            clickEvent,
            groupCartId,
            isMROCart,
            syncApplyCouponCart,
        ],
    );

    const onExtraSavingsTooltipClick = useCallback(
        (applyCouponWidgetData?: ApplyCouponWidgetData) => {
            clickTooltipOffercashEvent(applyCouponWidgetData?.coupon_code ?? "", clickEvent);
        },
        [clickEvent],
    );

    return {
        onViewAllCouponsClick,
        onDirectApplyCouponCtaClick,
        onCouponLockedLineItemLoad,
        onDirectCouponLineItemLoad,
        onApplyCouponSectionClick,
        onExtraSavingsTooltipClick,
        goToCouponListing,
    };
};
